/**
 * Whole-folder uploads: turn a dropped or picked folder into its files, each
 * with the folder path it sat in, so an upload surface can rebuild the tree
 * (project resources) or simply take every file (client, supplier and person
 * resources, which have no subfolders).
 *
 * Two ways in:
 *  - drag & drop — `DataTransferItem.webkitGetAsEntry()` walks the dropped
 *    directories; without it a dropped folder arrives as one empty "file" that
 *    can only fail to upload.
 *  - the folder picker — `<input webkitdirectory>` hands over every file with
 *    `webkitRelativePath` ("Oferta/Anexe/plan.pdf") already filled in.
 */

export interface PickedFile {
  file: File;
  /** Folder the file sat in, relative to what was dropped ("Oferta/Anexe"); "" at the top. */
  dir: string;
}

export interface PickedTree {
  files: PickedFile[];
  /** Every folder in what was dropped, empty ones included ("Oferta", "Oferta/Anexe"). */
  dirs: string[];
}

/**
 * Spread onto an `<input type="file">` to make it pick a folder. The attribute
 * is not in React's typings, but React passes unknown lowercase attributes
 * through to the DOM.
 */
export const DIRECTORY_INPUT_PROPS = { webkitdirectory: '', directory: '' } as Record<
  string,
  string
>;

// OS clutter that rides along inside folders and nobody means to upload.
const JUNK_NAMES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini', '.localized']);

function isJunk(name: string): boolean {
  return JUNK_NAMES.has(name) || name.startsWith('._');
}

function dirOf(relativePath: string): string {
  const i = relativePath.lastIndexOf('/');
  return i < 0 ? '' : relativePath.slice(0, i);
}

/** Every ancestor of each folder too, so the list is closed under "parent of". */
function withAncestors(dirs: Iterable<string>): string[] {
  const all = new Set<string>();
  for (const dir of dirs) {
    const parts = dir.split('/').filter(Boolean);
    for (let i = 1; i <= parts.length; i += 1) all.add(parts.slice(0, i).join('/'));
  }
  return [...all].sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b));
}

/** Files from a folder picker (`webkitdirectory`) or a plain multi-file picker. */
export function filesFromInput(list: FileList | File[]): PickedTree {
  const files: PickedFile[] = [];
  for (const file of Array.from(list)) {
    if (isJunk(file.name)) continue;
    files.push({ file, dir: dirOf(file.webkitRelativePath || '') });
  }
  return { files, dirs: withAncestors(files.map((f) => f.dir).filter(Boolean)) };
}

function readAllEntries(dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = dir.createReader();
  const out: FileSystemEntry[] = [];
  // readEntries hands back at most ~100 entries per call; keep asking until
  // it returns an empty batch.
  return new Promise((resolve, reject) => {
    const next = () =>
      reader.readEntries((batch) => {
        if (batch.length === 0) resolve(out);
        else {
          out.push(...batch);
          next();
        }
      }, reject);
    next();
  });
}

function entryFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/**
 * Files (and folders) from a drop. Must be called inside the drop handler,
 * before any `await`: the browser empties `dataTransfer` once the event ends,
 * so the entries are taken synchronously and walked afterwards.
 */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<PickedTree> {
  const items = Array.from(dt.items ?? []);
  const entries = items
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.() ?? null);
  if (entries.length === 0 || entries.some((e) => e === null)) {
    // No entry API (or a non-file item): fall back to the flat file list.
    return filesFromInput(Array.from(dt.files ?? []));
  }

  const files: PickedFile[] = [];
  const dirs: string[] = [];
  async function walk(entry: FileSystemEntry, parent: string): Promise<void> {
    if (isJunk(entry.name)) return;
    if (entry.isFile) {
      files.push({ file: await entryFile(entry as FileSystemFileEntry), dir: parent });
      return;
    }
    if (entry.isDirectory) {
      const path = parent ? `${parent}/${entry.name}` : entry.name;
      dirs.push(path);
      for (const child of await readAllEntries(entry as FileSystemDirectoryEntry)) {
        await walk(child, path);
      }
    }
  }
  for (const entry of entries) await walk(entry as FileSystemEntry, '');
  return { files, dirs: withAncestors(dirs) };
}
